/**
 * Company Hub — top-level category hub for /company.
 * Cross-references content/company/*.md
 *
 * Lists 10+ company pages:
 *  /about, /careers, /leadership, /contact, /demo,
 *  /history, /mission, /partners, /press, /status, /support
 */

export const companyHubMeta = {
  title: "CryptoMize | The Organization Behind Perception X2",
  description:
    "The company that built and operates Perception X2. 15+ years of autonomous perception amplification excellence. 300+ elite clients. Five proprietary AI engines. Built on precision, discretion, and measurable impact.",
  canonical: "https://perception.ac/company",
  slug: "company",
};

export interface CompanyChild {
  code: string;
  title: string;
  description: string;
  href: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const companyChildren: CompanyChild[] = [
  {
    code: "01",
    title: "About CryptoMize",
    description:
      "Who we are, what we built, and why. The organization behind Perception X2 — 15+ years of operational excellence.",
    href: "/about",
    icon: "Building2",
    accent: "crimson",
  },
  {
    code: "02",
    title: "Mission",
    description:
      "Autonomous perception amplification at global scale. The mission that has driven CryptoMize for 15+ years.",
    href: "/mission",
    icon: "Target",
    accent: "purple",
  },
  {
    code: "03",
    title: "History",
    description:
      "From vision to global platform. The journey of CryptoMize and the evolution of the Perception X2 stack.",
    href: "/history",
    icon: "History",
    accent: "cyan",
  },
  {
    code: "04",
    title: "Leadership",
    description:
      "The executives, engineers, and strategists who shape CryptoMize and the Perception X2 platform.",
    href: "/leadership",
    icon: "Users",
    accent: "emerald",
  },
  {
    code: "05",
    title: "Careers",
    description:
      "Join the team building the world's most advanced perception amplification platform. Open roles across engineering and operations.",
    href: "/careers",
    icon: "Briefcase",
    accent: "amber",
  },
  {
    code: "06",
    title: "Partners",
    description:
      "Strategic, technology, and channel partnerships. Engage with the Perception X2 ecosystem.",
    href: "/partners",
    icon: "Handshake",
    accent: "denim",
  },
  {
    code: "07",
    title: "Press",
    description:
      "Media coverage, press releases, and editorial resources. The Perception X2 story in the public record.",
    href: "/press",
    icon: "Newspaper",
    accent: "violet",
  },
  {
    code: "08",
    title: "Contact",
    description:
      "Speak with our team. Engagement inquiries, partnerships, and media contact in one place.",
    href: "/contact",
    icon: "Mail",
    accent: "rose",
  },
  {
    code: "09",
    title: "Request a Demo",
    description:
      "See Perception X2 in operation. Schedule a live demonstration with our engagement team.",
    href: "/demo",
    icon: "PlayCircle",
    accent: "crimson",
  },
  {
    code: "10",
    title: "Status",
    description:
      "Platform status, incident history, and operational telemetry. Transparent availability reporting.",
    href: "/status",
    icon: "Activity",
    accent: "emerald",
  },
  {
    code: "11",
    title: "Support",
    description:
      "Customer support, technical assistance, and operational escalation. 24/7 coverage for active engagements.",
    href: "/support",
    icon: "LifeBuoy",
    accent: "cyan",
  },
];

export interface CompanyStat {
  value: string;
  label: string;
  to: number;
  suffix?: string;
  decimals?: number;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet";
}

export const companyStats: CompanyStat[] = [
  { value: "15+", label: "Years of Excellence", to: 15, suffix: "+", icon: "Calendar", accent: "crimson" },
  { value: "300+", label: "Elite Global Clients", to: 300, suffix: "+", icon: "Users", accent: "purple" },
  { value: "5", label: "Proprietary Engines", to: 5, icon: "Cpu", accent: "cyan" },
  { value: "50+", label: "Platform Coverage", to: 50, suffix: "+", icon: "Globe", accent: "emerald" },
  { value: "0", label: "Security Incidents", to: 0, icon: "Shield", accent: "amber" },
];

export interface CompanyFeature {
  title: string;
  description: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const companyFeatures: CompanyFeature[] = [
  {
    title: "Engineering-First Culture",
    description: "We are an engineering company that builds and operates the platform — not a marketing firm or PR agency.",
    icon: "Cpu",
    accent: "crimson",
  },
  {
    title: "Defense-Grade Discipline",
    description: "Precision, discretion, and rigor at every layer. The operational standards of a defense contractor.",
    icon: "Shield",
    accent: "purple",
  },
  {
    title: "Technology-Pioneer Velocity",
    description: "The innovation pace of a venture-backed technology company. Quarterly platform releases, continuous refinement.",
    icon: "Rocket",
    accent: "cyan",
  },
  {
    title: "Sovereign-Grade Trust",
    description: "300+ elite clients including governments, Fortune 500, and global institutions trust us with their strategic perception.",
    icon: "Crown",
    accent: "emerald",
  },
  {
    title: "Measurable Outcomes",
    description: "Every engagement is validated by data. 300–500% narrative penetration, 95–98% organic authenticity — measured, not promised.",
    icon: "LineChart",
    accent: "amber",
  },
  {
    title: "Global Operations",
    description: "Multi-region active-active deployment, 15+ language support, 24/7 operations across every engagement.",
    icon: "Globe",
    accent: "denim",
  },
  {
    title: "Long-Term Relationships",
    description: "The average enterprise engagement exceeds 4 years. We measure success in decades, not quarters.",
    icon: "Heart",
    accent: "violet",
  },
  {
    title: "Continuous Investment",
    description: "Significant ongoing investment in proprietary technology, security infrastructure, and operational capability.",
    icon: "TrendingUp",
    accent: "rose",
  },
];

export interface CompanyUseCase {
  title: string;
  body: string;
  icon: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const companyUseCases: CompanyUseCase[] = [
  {
    title: "Enterprise Engagement",
    body: "Fortune 500 brand defense, market launch amplification, and competitive perception engineering. Multi-year engagements.",
    icon: "Building",
    accent: "crimson",
  },
  {
    title: "Sovereign Operations",
    body: "National-scale perception engineering for governments, diplomatic missions, and public institutions. Sovereign-grade data residency.",
    icon: "Landmark",
    accent: "purple",
  },
  {
    title: "Public Figure Engineering",
    body: "Personal brand protection and amplification for executives, public figures, and high-net-worth individuals. Discretion-first engagements.",
    icon: "Crown",
    accent: "cyan",
  },
  {
    title: "Crisis Containment",
    body: "Pre-built playbooks for category-killing events. Operational in under 60 minutes from signal to deployed response.",
    icon: "Siren",
    accent: "emerald",
  },
  {
    title: "Channel Partnerships",
    body: "White-label and co-engineered deployments with strategic partners across marketing, communications, and consulting.",
    icon: "Handshake",
    accent: "amber",
  },
  {
    title: "Technology Licensing",
    body: "Limited, vetted licensing of proprietary engines for strategic partners with mission-aligned use cases.",
    icon: "Key",
    accent: "denim",
  },
];

export interface CompanyWhy {
  title: string;
  body: string;
  metric?: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const companyWhy: CompanyWhy[] = [
  {
    title: "15+ Years of Operational Excellence",
    body: "CryptoMize has been building perception amplification systems since long before the technology was mainstream. We have seen what works and what doesn't — across 300+ engagements.",
    metric: "15+",
    accent: "crimson",
  },
  {
    title: "Engineering-First DNA",
    body: "We are an engineering company. Our leadership team has shipped production AI systems, scaled petabyte infrastructures, and built the perception management category.",
    metric: "✓",
    accent: "purple",
  },
  {
    title: "Proprietary Technology",
    body: "Five engines, 15+ years of refinement, zero licensing dependencies. No white-label risk. No third-party model risk. No vendor lock-in.",
    metric: "5",
    accent: "cyan",
  },
  {
    title: "Zero Security Incidents",
    body: "15+ years of operations without a single breach. The same standards used by defense and intelligence agencies protect every engagement.",
    metric: "0",
    accent: "emerald",
  },
  {
    title: "300+ Elite Clients",
    body: "Governments, Fortune 500 enterprises, sovereign institutions, and global public figures trust CryptoMize with their strategic perception.",
    metric: "300+",
    accent: "amber",
  },
  {
    title: "Sustained Innovation",
    body: "Quarterly platform releases, continuous model refinement, ongoing security investment. We are not standing still — neither is the threat landscape.",
    metric: "✓",
    accent: "denim",
  },
];

export interface CompanyFaq {
  q: string;
  a: string;
}

export const companyFaq: CompanyFaq[] = [
  {
    q: "What is CryptoMize?",
    a: "CryptoMize is the organization that built and operates Perception X2 — the world's most advanced autonomous perception amplification platform. We are an engineering company dedicated to one mission: autonomous perception amplification at global scale. We are not a marketing agency. We are not a PR firm. We are a technology organization.",
  },
  {
    q: "How long has CryptoMize been operating?",
    a: "CryptoMize has been operating for 15+ years. The Perception X2 platform represents the cumulative engineering of the entire period — every engagement, every model refinement, every security hardening baked into the current stack.",
  },
  {
    q: "Who are CryptoMize's clients?",
    a: "CryptoMize serves 300+ elite clients across governments, Fortune 500 enterprises, sovereign institutions, and global public figures. We do not publish a client list — discretion is foundational to every engagement.",
  },
  {
    q: "What makes CryptoMize different from a marketing agency?",
    a: "CryptoMize is an engineering company. We build and operate the technology platform, not campaigns. Our staff includes AI researchers, distributed systems engineers, and security specialists. Our outputs are measured in narrative penetration and perception shift — not impressions or engagement metrics.",
  },
  {
    q: "How can I contact CryptoMize?",
    a: "Engagement inquiries, partnerships, and media contact are all routed through /contact. Active clients can reach our 24/7 support line through /support. Press inquiries are handled through /press.",
  },
  {
    q: "Does CryptoMize have open roles?",
    a: "Yes. We hire across engineering, security, client operations, and strategic roles. Open positions are listed at /careers. We prefer to hire engineers with production AI experience and operators with high-stakes operational backgrounds.",
  },
  {
    q: "Where is CryptoMize headquartered?",
    a: "CryptoMize is a globally distributed organization with engineering and operations presence across multiple regions. Specific location details are not published for operational security reasons.",
  },
  {
    q: "How does CryptoMize handle partnerships?",
    a: "Strategic, technology, and channel partnerships are managed through /partners. We engage in limited, vetted partnerships with mission-aligned organizations. White-label deployments are available for select strategic partners.",
  },
  {
    q: "What is the engagement process?",
    a: "Engagements begin with a confidential strategic briefing. From there, our team develops a customized deployment architecture and presents a calibrated engagement model. Standard engagements reach operational status in 4–6 weeks. Crisis-class deployments can be live in under 60 minutes.",
  },
  {
    q: "Does CryptoMize publish a client list or case studies?",
    a: "No. Discretion is foundational to every engagement, and we do not publish client lists, case studies, or engagement details without explicit written consent. Aggregate performance metrics are available in our public materials.",
  },
];

export const companyCta = {
  eyebrow: "Engage with CryptoMize",
  title: "The organization behind Perception X2.",
  description:
    "Speak with our team about your strategic perception requirements. Engagement inquiries, partnerships, media contact, and support all routed through the appropriate channel.",
  primary: { label: "Contact Our Team", href: "/contact" },
  secondary: { label: "Request a Demo", href: "/demo" },
  trustItems: [
    "15+ years operating",
    "300+ elite clients",
    "5 proprietary engines",
    "50+ platforms",
    "Zero security incidents",
  ],
};

/* Company timeline — bespoke visual data */
export interface TimelineMilestone {
  year: string;
  title: string;
  body: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const timeline: TimelineMilestone[] = [
  {
    year: "Y0",
    title: "Founding Insight",
    body: "Perception is engineered, not received. CryptoMize is founded on the principle that the organizations with the strongest digital presence engineer perception systematically.",
    accent: "crimson",
  },
  {
    year: "Y3",
    title: "First Engine",
    body: "Organic Simulation Engine reaches production maturity. First enterprise engagements validate the agent-based propagation approach.",
    accent: "purple",
  },
  {
    year: "Y6",
    title: "Five-Engine Stack",
    body: "Narrative Generation, Cognitive Resonance, Echo Chamber Architecture, and Autonomous Operations engines reach production. The composed stack is operational.",
    accent: "cyan",
  },
  {
    year: "Y9",
    title: "Sovereign-Grade",
    body: "First sovereign deployment. Multi-region active-active architecture with cryptographic isolation. SOC 2 Type II and ISO 27001 certifications achieved.",
    accent: "emerald",
  },
  {
    year: "Y12",
    title: "144-Dimensional Resonance",
    body: "Cognitive Resonance Engine reaches 144-dimensional emotional and cognitive modeling. Sub-second stream processing at petabyte scale.",
    accent: "amber",
  },
  {
    year: "Y15+",
    title: "Industry Standard",
    body: "300+ elite clients. Zero security incidents. The Perception X2 platform is the reference standard for autonomous perception amplification.",
    accent: "denim",
  },
];

/* Leadership roles — bespoke visual data */
export interface LeadershipRole {
  name: string;
  role: string;
  focus: string;
  accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim" | "violet" | "rose";
}

export const leadershipRoles: LeadershipRole[] = [
  { name: "Executive Leadership", role: "Office of the CEO", focus: "Strategic direction, partnerships, and external relations.", accent: "crimson" },
  { name: "Engineering", role: "VP, Engineering", focus: "Platform architecture, model development, infrastructure.", accent: "cyan" },
  { name: "Security", role: "Chief Information Security Officer", focus: "Defense-grade security, compliance, audit, and incident response.", accent: "denim" },
  { name: "Engagement", role: "VP, Client Operations", focus: "Engagement delivery, account strategy, and operational excellence.", accent: "emerald" },
  { name: "Research", role: "VP, Research", focus: "AI/ML research, cognitive modeling, and capability roadmap.", accent: "purple" },
  { name: "Operations", role: "VP, Operations", focus: "24/7 platform operations, autonomous orchestration, and reliability.", accent: "amber" },
];
